import * as React from 'react';
import { CoreDropdown } from '../internal/WixUiCoreComponents/components/CoreDropdown';
import { DropdownOption } from '../Dropdown/DropdownOption';
import { useTPAComponentsContext } from '../TPAComponentsConfig';
import { st, classes } from './FloatingDropdown.st.css';
import { FloatingDropdownBase } from './FloatingDropdownBase';
import { DATA_HOOKS } from './constants';
import {
  Option,
  OptionFactory,
} from '../internal/WixUiCoreComponents/components/CoreDropdownOption';
import { createOptionId, modifyOptions } from '../Dropdown/helpers';
import uniqueId from '../../common/uniqueId';

import classnames from 'classnames';
import { deprecationLog } from '../../common/deprecationLog';
import { FloatingDropdownProps } from './types';
/* TODO
 * The import below is a workaround. FloatingDropdown and Dropdown
 * components should be refactored according to this thread
 * https://github.com/wix/wix-ui-tpa/pull/387 (or merged into a single component)
 */
import { classes as dropdownClasses } from '../Dropdown/Dropdown.st.css';
import { useMenuPatternKeys } from './useMenuPatternKeys';
import { DropdownOptionTextOverflow } from '../Dropdown/types';

/** Dropdown component for sort. */
export const FloatingDropdown = (props: FloatingDropdownProps) => {
  const { removeElementMinWidth, onChange, options, optionsContainerId } =
    props;
  const { mobile } = useTPAComponentsContext();

  const containerId = React.useMemo(
    () =>
      optionsContainerId ?? uniqueId('floating-dropdown-options-container_'),
    [optionsContainerId],
  );
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [focusedOptionId, setFocusedOptionId] = React.useState<string | null>(
    null,
  );

  React.useEffect(() => {
    if (!removeElementMinWidth) {
      deprecationLog(
        'FloatingDropdown',
        'The current `FloatingDropdown` component API will be deprecated in a future version. Please use the `removeElementMinWidth` prop in order to use the new API.',
      );
    }
  }, []);

  const onSelect = (id: string) => {
    if (onChange) {
      const newSelectedOption = options.find((option) => option.id === id);
      if (newSelectedOption) {
        onChange(newSelectedOption);
        setIsExpanded(false);
      }
    }
  };

  const coreOptions = options.map((option) =>
    option.divider
      ? OptionFactory.createDivider({ className: classes.optionDivider })
      : OptionFactory.create({
          ...option,
          role: 'menuitem',
          isSelectable:
            option.unavailable && option.isSelectable === undefined
              ? true
              : option.isSelectable,

          render: () => (
            <DropdownOption
              className={classes.option}
              {...option}
              textOverflow={DropdownOptionTextOverflow.wrap}
              isFocused={focusedOptionId === option.id}
              descendantId={createOptionId(containerId, option.id)}
            />
          ),
        }),
  );

  const { handleKeydown } = useMenuPatternKeys({
    options: options.filter((option) => !option.divider),
    onSelect,
    isOpen: isExpanded,
    setIsOpen: setIsExpanded,
    focusedOptionId,
    setFocusedOptionId,
  });

  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'data-hook': dataHook,
    disabled,
    forceContentElementVisibility,
    label,
    placeholder,
    value,
    name,
    id,
    placement,
    appendTo,
    displayBlock,
    dynamicWidth,
    className,
  } = props;

  const popoverPlacement = placement
    ? placement
    : removeElementMinWidth
    ? 'bottom-start'
    : 'bottom';

  const baseElement = (
    <FloatingDropdownBase
      className={classes.floatingDropdownBase}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      disabled={disabled}
      label={label}
      placeholder={placeholder}
      value={value}
      name={name}
      mobile={mobile}
      options={modifyOptions(options)}
      id={id}
      isExpanded={isExpanded}
      onChange={onSelect}
      displayBlock={displayBlock}
      aria-controls={containerId}
    />
  );

  return (
    <div
      data-mobile={mobile}
      data-hook={dataHook}
      className={st(
        classes.root,
        { mobile, removeElementMinWidth },
        classnames(classes.overrideStyleParams, className, {
          [classes.displayInline]: !displayBlock,
        }),
      )}
    >
      {mobile ? (
        baseElement
      ) : (
        <CoreDropdown
          className={classes.dropdown}
          popoverProps={{
            contentClassName: dropdownClasses.dropdownContainer,
            appendTo,
            dynamicWidth: dynamicWidth || appendTo === 'window',
            placement: popoverPlacement,
          }}
          contentProps={{
            id: containerId,
            role: 'menu',
            activeDescendant:
              focusedOptionId && createOptionId(containerId, focusedOptionId),
          }}
          data-hook={DATA_HOOKS.coreDropdown}
          data-mobile={mobile}
          forceContentElementVisibility={forceContentElementVisibility}
          selectedId={value}
          onSelect={(selectedCoreOption: Option) => {
            if (selectedCoreOption) {
              onSelect(String(selectedCoreOption.id));
            }
          }}
          disabled={disabled}
          onKeyDown={handleKeydown}
          options={coreOptions}
          isOpen={isExpanded}
          setIsOpen={setIsExpanded}
        >
          {baseElement}
        </CoreDropdown>
      )}
    </div>
  );
};
